import { useQuery } from '@tanstack/react-query';
import { Download, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { API_BASE } from '@/lib/api';
import { KOPIA_PORTALU_POBIERZ, fetchSpisKopiiPortalu } from '@/lib/kopia-portalu-api';
import { useI18n } from '@/lib/i18n';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  KOPIA PORTALU — nasza strona rozjazdu, do pobrania obok kopii bazy SPS.
 *
 *  ⚠️⚠️ ZAMÓWIONE 22.09.2026: „byłoby znacznie prościej coś pobrać do sprawdzenia i porównania baz
 *  bez zgadywania". Kopię bazy klienta portal umie oddać od zawsze; swojej własnej strony — nie.
 *  Przez to każde pytanie „czy portal ma to samo co master" kończyło się liczeniem sum.
 *
 *  ⚠️⚠️ SPIS PRZED POBRANIEM, NIE PO. Paczka niesie dane osobowe kadry klienta, więc nie powstaje
 *  po to, żeby ktoś dowiedział się, co w niej jest. Liczby wierszy widać z ekranu, plik dopiero
 *  po kliknięciu.
 *
 *  ⚠️ NAZWA „KOPIA" JEST TU MYLĄCA I DLATEGO EKRAN JĄ PROSTUJE. To nie jest kopia do odtworzenia:
 *  nie ma w niej kolejki zmian, dziennika ani agentów. Ktoś, kto weźmie ją za backup portalu,
 *  dowie się o tym dopiero wtedy, gdy będzie potrzebował prawdziwego.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function KopiaPortalu({ groupId }: { groupId: string }) {
  const { t } = useI18n();

  const spis = useQuery({
    queryKey: ['kopia-portalu-spis', groupId],
    queryFn: () => fetchSpisKopiiPortalu(groupId),
    enabled: groupId !== '',
  });

  const pliki = spis.data?.pliki ?? [];
  const wierszyRazem = pliki.reduce((s, p) => s + p.wierszy, 0);

  return (
    <Card>
      <CardContent className="space-y-3 pt-5">
        <div>
          <h2 className="text-sm font-semibold">{t('kopiaPortalu.tytul')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('kopiaPortalu.poCo')}</p>
        </div>

        {groupId === '' ? (
          <p className="text-sm text-muted-foreground">{t('kopiaPortalu.brakWyspy')}</p>
        ) : (
          <>
            {/*
              ⚠️⚠️ CO DOKŁADNIE WYJDZIE W PLIKU — nazwy arkuszy i liczby wierszy. To jest ta sama
              zasada, co przy przyciskach stanu: człowiek ma wiedzieć, co pobiera, ZANIM pobierze.
            */}
            <p className="text-xs text-muted-foreground">
              {t('kopiaPortalu.zawartosc')
                .replace('{n}', String(pliki.length))
                .replace('{w}', String(wierszyRazem))}
            </p>

            <ul className="grid gap-x-6 gap-y-0.5 text-[11px] text-muted-foreground sm:grid-cols-2">
              {pliki.map((p) => (
                <li key={p.plik} className="flex items-baseline justify-between gap-2">
                  <span className="font-mono">{p.plik}</span>
                  <span className="tabular-nums">{p.wierszy}</span>
                </li>
              ))}
            </ul>

            {/* ⚠️ Dane osobowe zapowiedziane, a nie odkryte przy otwieraniu pliku. */}
            <p className="flex items-start gap-2 text-xs text-[var(--warning)]">
              <ShieldAlert className="mt-0.5 size-3.5 shrink-0" />
              {t('kopiaPortalu.osobowe')}
            </p>

            {/*
              ⚠️ ZWYKŁY ODNOŚNIK Z CIASTECZKIEM SESJI, nie `fetch`. Tak samo pobiera się kopie baz:
              przeciąganie megabajtów przez pamięć karty po to, żeby zaraz zapisać je na dysk,
              nie daje nic poza możliwością zawieszenia przeglądarki.
            */}
            {/*
              ⚠️ PRZYCISK POJAWIA SIĘ DOPIERO ZE SPISEM, zamiast stać wyszarzony. `disabled` na
              odnośniku niczego nie blokuje — byłby napisem, który kłamie o tym, co się stanie
              po kliknięciu.
            */}
            {pliki.length > 0 && (
              <Button asChild size="sm" variant="outline">
                <a href={`${API_BASE}${KOPIA_PORTALU_POBIERZ}?groupId=${encodeURIComponent(groupId)}`}>
                  <Download className="size-4" />
                  {t('kopiaPortalu.pobierz')}
                </a>
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
